<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { api } from '../api'
import AdminNav from '../components/AdminNav.vue'
import Quill from '../utils/quill'

const route = useRoute()
const router = useRouter()
const id = route.params.id
const isNew = id === 'new'
const form = ref({
  slug: '',
  title: '',
  content: '',
  language: 'el',
  translations: { en: { title: '', content: '' } },
  is_default: false,
  show_in_menu: true,
  menu_position: 'header',
  menu_order: 0
})
const loading = ref(!isNew)
const saving = ref(false)
const error = ref('')
const savedFlash = ref(false)
const errorFlash = ref(false)
const activeLangTab = ref('el')
const previewHTML = computed(() =>
  activeLangTab.value === 'el' ? form.value.content : form.value.translations.en.content || ''
)
let quill
const editorContainer = ref(null)
const toolbarOptions = [
  [{ header: [1, 2, 3, 4, 5, false] }],
  [{ font: [] }],
  [{ size: ['small', false, 'large', 'huge'] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ script: 'sub' }, { script: 'super' }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  [{ align: [] }],
  ['blockquote', 'code-block'],
  ['link', 'image', 'video'],
  ['clean']
]

function deltaToHTML(delta) {
  // Basic conversion (Quill offers getHTML in v2; fallback manual)
  const temp = document.createElement('div')
  const q = new Quill(document.createElement('div')) // temp instance for built-in clipboard
  q.setContents(delta)
  temp.innerHTML = q.root.innerHTML
  return temp.innerHTML
}

function loadCurrentContentIntoEditor() {
  if (!quill) return
  const html =
    activeLangTab.value === 'el' ? form.value.content : form.value.translations.en.content
  // Quill 2 can set html via dangerouslyPasteHTML
  quill.setContents([])
  quill.clipboard.dangerouslyPasteHTML(html || '')
}

watch(activeLangTab, () => {
  loadCurrentContentIntoEditor()
})

function ensureTranslationStructure(obj) {
  if (!obj.translations || typeof obj.translations !== 'object') obj.translations = {}
  if (!obj.translations.en) obj.translations.en = { title: '', content: '' }
  else {
    obj.translations.en.title = obj.translations.en.title || ''
    obj.translations.en.content = obj.translations.en.content || ''
  }
}

async function load() {
  if (isNew) return
  try {
    const { data } = await api.get('/pages/by-id/' + id)
    if (data.slug === 'gallery') {
      alert('This page is managed by the custom Gallery module and cannot be edited generally.')
      router.replace('/admin/gallery')
      return
    }
    const t = data.translations && typeof data.translations === 'object' ? data.translations : {}
    const merged = { ...t, en: { title: '', content: '', ...(t.en || {}) } }
    form.value = {
      slug: data.slug,
      title: data.title,
      content: data.content,
      language: data.language,
      translations: merged,
      is_default: data.is_default ?? false,
      show_in_menu: data.show_in_menu ?? true,
      menu_position: data.menu_position ?? 'header',
      menu_order: data.menu_order ?? 0
    }
  } catch {
    error.value = 'not_found'
  } finally {
    ensureTranslationStructure(form.value)
    loading.value = false
  }
}

async function save() {
  // Sync Quill content before saving to capture any pending changes (e.g. resizes)
  if (quill) {
    const html = quill.root.innerHTML === '<p><br></p>' ? '' : quill.root.innerHTML
    if (activeLangTab.value === 'el') form.value.content = html
    else form.value.translations.en.content = html
  }

  saving.value = true
  error.value = ''
  errorFlash.value = false
  try {
    ensureTranslationStructure(form.value)
    if (isNew) {
      const { data } = await api.post('/pages', form.value)
      router.replace('/admin/pages/' + data.id)
    } else {
      await api.put('/pages/' + id, form.value)
    }
    savedFlash.value = true
    setTimeout(() => {
      savedFlash.value = false
      router.push('/admin/pages')
    }, 1000)
  } catch (e) {
    error.value = e?.response?.data?.detail === 'Slug already exists' ? 'slug_exists' : 'save_error'
    errorFlash.value = true
    setTimeout(() => (errorFlash.value = false), 3000)
  } finally {
    saving.value = false
  }
}

onMounted(() => {
  if (!isNew) load()
  else loading.value = false
})
async function initQuillIfNeeded() {
  if (quill || loading.value) return
  await nextTick()
  const el = editorContainer.value
  if (!el) return
  quill = new Quill(el, {
    theme: 'snow',
    modules: {
      toolbar: toolbarOptions,
      history: { delay: 500, maxStack: 200, userOnly: true },
      blotFormatter: {}
    }
  })
  quill.on('text-change', () => {
    const html = quill.root.innerHTML === '<p><br></p>' ? '' : quill.root.innerHTML
    if (activeLangTab.value === 'el') form.value.content = html
    else form.value.translations.en.content = html
  })
  loadCurrentContentIntoEditor()
}

onMounted(() => {
  initQuillIfNeeded()
})
watch(loading, (v) => {
  if (!v) initQuillIfNeeded()
})
</script>

<template>
  <section style="max-width: 800px">
    <div class="admin-nav-wrapper">
      <AdminNav />
    </div>

    <div class="header sticky-header">
      <h2>{{ isNew ? $t('admin.pageNew') : $t('admin.pageEdit') }}</h2>
      <div class="actions">
        <button
          type="button"
          class="btn"
          @click="router.push('/admin/pages')"
          style="margin-right: 0.5rem"
        >
          {{ $t('common.cancel') }}
        </button>
        <button type="submit" form="page-form" class="btn-primary" :disabled="saving">
          {{ saving ? $t('common.saving') : $t('common.save') }}
        </button>
      </div>
    </div>

    <p v-if="loading">{{ $t('common.loading') }}</p>
    <p v-else-if="error" style="color: #c00">
      {{ $t(error === 'not_found' ? 'common.notFound' : 'common.errorLoading') }}
    </p>
    <form
      id="page-form"
      v-else
      @submit.prevent="save"
      style="display: flex; flex-direction: column; gap: 0.6rem"
    >
      <label
        >{{ $t('page.slug') }}
        <input v-model="form.slug" required :disabled="!isNew" />
      </label>
      <label
        >{{ $t('page.elTitle') }}
        <input v-model="form.title" required />
      </label>
      <label
        >{{ $t('page.elLanguage') }}
        <input v-model="form.language" required />
      </label>
      <div class="tab-bar">
        <button
          type="button"
          :class="{ active: activeLangTab === 'el' }"
          @click="activeLangTab = 'el'"
        >
          EL
        </button>
        <button
          type="button"
          :class="{ active: activeLangTab === 'en' }"
          @click="activeLangTab = 'en'"
        >
          EN
        </button>
      </div>
      <div class="editor-layout">
        <div class="editor-pane">
          <div ref="editorContainer" class="quill-wrapper"></div>
        </div>
        <div class="preview-pane" :data-label="$t('common.preview')">
          <div class="preview-inner" v-html="previewHTML"></div>
        </div>
      </div>
      <fieldset style="border: 1px solid var(--border); padding: 0.75rem; margin-top: 1rem">
        <legend>{{ $t('admin.enTranslation') }}</legend>
        <div class="grid-2">
          <label
            >{{ $t('page.enTitle') }}
            <input v-model="form.translations.en.title" />
          </label>
        </div>
      </fieldset>
      <fieldset style="border: 1px solid var(--border); padding: 0.75rem; margin-top: 1rem">
        <legend>{{ $t('admin.menuSettings') }}</legend>
        <div class="menu-config">
          <label class="checkbox-label">
            <input type="checkbox" v-model="form.is_default" :disabled="true" />
            <span>{{ $t('admin.isDefaultPage') }}</span>
            <small style="opacity: 0.7; display: block; margin-top: 0.2rem">{{
              $t('admin.defaultPageHelp')
            }}</small>
          </label>
          <label class="checkbox-label">
            <input type="checkbox" v-model="form.show_in_menu" />
            <span>{{ $t('admin.showInMenu') }}</span>
            <small style="opacity: 0.7; display: block; margin-top: 0.2rem">{{
              $t('admin.showInMenuHelp')
            }}</small>
          </label>
          <label v-if="form.show_in_menu"
            >{{ $t('admin.menuPosition') }}
            <select v-model="form.menu_position">
              <option value="header">{{ $t('admin.menuHeader') }}</option>
              <option value="footer">{{ $t('admin.menuFooter') }}</option>
              <option :value="null">{{ $t('admin.menuNone') }}</option>
            </select>
            <small style="opacity: 0.7; display: block; margin-top: 0.2rem">{{
              $t('admin.menuPositionHelp')
            }}</small>
          </label>
          <label v-if="form.show_in_menu"
            >{{ $t('admin.menuOrder') }}
            <input type="number" v-model.number="form.menu_order" min="0" step="1" />
            <small style="opacity: 0.7; display: block; margin-top: 0.2rem">{{
              $t('admin.menuOrderHelp')
            }}</small>
          </label>
        </div>
      </fieldset>
      <p v-if="error" style="color: #c00">
        {{
          error === 'save_error'
            ? $t('common.errorSaving')
            : error === 'slug_exists'
              ? $t('admin.slugExists')
              : error === 'not_found'
                ? $t('common.notFound')
                : $t('common.errorLoading')
        }}
      </p>
    </form>

    <transition name="fade">
      <div v-if="savedFlash" class="flash success">✓ {{ $t('common.saved') }}</div>
    </transition>
    <transition name="fade">
      <div v-if="errorFlash" class="flash error">✗ {{ $t('common.saveFailed') }}</div>
    </transition>
  </section>
</template>

<style scoped>
input,
textarea {
  width: 100%;
  box-sizing: border-box;
}
label {
  display: flex;
  flex-direction: column;
  font-size: 0.85rem;
  gap: 0.2rem;
}
.tab-bar {
  display: flex;
  gap: 0.5rem;
  margin: 0.5rem 0 1rem;
}
.tab-bar button {
  background: var(--inner);
  color: #ddd;
  padding: 0.4rem 0.9rem;
  border: 1px solid var(--border);
  cursor: pointer;
  border-radius: 4px;
  font-size: 0.75rem;
  letter-spacing: 0.5px;
}
.tab-bar button.active {
  background: rgb(224, 126, 39);
  color: #000;
  border-color: rgb(224, 126, 39);
}
.editor-layout {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}
.editor-pane {
  flex: 1 1 480px;
  min-width: 300px;
}
.quill-wrapper {
  height: 420px;
  display: flex;
  flex-direction: column;
}
.quill-wrapper .ql-toolbar {
  background: var(--inner);
  border: 1px solid var(--border);
}
.quill-wrapper .ql-container {
  flex: 1;
  background: var(--inner);
  border: 1px solid var(--border);
  border-top: none;
  color: #eee;
}
.quill-wrapper .ql-editor {
  min-height: 320px;
}
.preview-pane {
  flex: 1 1 420px;
  min-width: 280px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--inner);
  max-height: 420px;
  overflow: auto;
  position: relative;
}
.preview-pane::before {
  content: attr(data-label);
  position: absolute;
  top: 4px;
  right: 8px;
  font-size: 0.65rem;
  letter-spacing: 0.5px;
  opacity: 0.6;
}
.preview-inner {
  padding: 1rem;
  font-size: 0.9rem;
  line-height: 1.5;
}
.preview-inner h1,
.preview-inner h2,
.preview-inner h3 {
  margin-top: 1.2em;
}
.grid-2 {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}
.menu-config {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.checkbox-label {
  flex-direction: row !important;
  align-items: flex-start;
  gap: 0.5rem;
}
.checkbox-label input[type='checkbox'] {
  width: auto;
  margin-top: 0.2rem;
}
.checkbox-label span {
  font-weight: 500;
}
select {
  width: 100%;
  box-sizing: border-box;
  padding: 0.4rem;
  background: var(--inner);
  color: #eee;
  border: 1px solid var(--border);
  border-radius: 4px;
}
</style>
